import {
  Archive,
  Check,
  Clock,
  FlaskConical,
  Layers,
  Loader2,
  Radar,
  ShieldCheck,
  UserCheck,
  Wrench,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * The product proof beside the headline: one change reaching a fleet, and what keeps it healthy
 * afterwards.
 *
 * ## Why the stages of a run, and not a list of features
 *
 * What is being sold is not "it can run a command". Plenty of things can. It is that a change to
 * twelve servers is checked on all twelve first, tried on one, confirmed by a person and rolled out
 * in batches — and that the fleet is watched, repaired and backed up after that. So the panel shows
 * those stages in the order they happen, and the headline beside it is illustrated by the mechanism
 * rather than by adjectives.
 *
 * ## It is a diagram, not a screenshot
 *
 * Deliberately not a pixel copy of the run page. A screenshot in a hero has to be retaken every time a
 * button moves, and at hero size it is unreadable anyway. This is built from the product's own
 * vocabulary — preflight, dry run, canary, batches — at a size that can be read in the second and a
 * half a visitor gives it.
 *
 * ## Nothing here is a performance claim
 *
 * No uptime percentage, no "updated in 4 seconds", no invented customer. Every value is a structural
 * fact about what the platform does. The fleet name and size are an example and read as one.
 */

interface Lane {
  icon: LucideIcon;
  label: string;
  /** The settled state. Structural, never a measurement nobody took. */
  state: string;
  /** One lane keeps moving after everything settles. See the pulse below. */
  live?: boolean;
}

const LANES: Lane[] = [
  { icon: ShieldCheck, label: 'Preflight', state: 'Access, sudo and package manager checked' },
  { icon: FlaskConical, label: 'Dry run', state: 'Reports what would change' },
  { icon: UserCheck, label: 'Canary', state: 'One server first, then you confirm' },
  { icon: Layers, label: 'Batches', state: 'The rest in batches, stopping on failure' },
  { icon: Radar, label: 'Monitoring', state: 'Checked from outside your network' },
  { icon: Wrench, label: 'Auto-Fix', state: 'Safe repairs before alerting you' },
  { icon: Archive, label: 'Backups', state: 'Restore-tested every week', live: true },
];

/** How long each lane waits before settling. Slow enough to read, quick enough to finish. */
const STEP_MS = 420;

export function HeroPanel() {
  const [settled, setSettled] = useState(0);

  useEffect(() => {
    // Runs once and stops. A loop would put motion in the corner of somebody's eye for as long as
    // they are trying to read the sentence beside it.
    if (settled >= LANES.length) {
      return;
    }

    const timer = window.setTimeout(() => setSettled((n) => n + 1), settled === 0 ? 320 : STEP_MS);

    return () => window.clearTimeout(timer);
  }, [settled]);

  const finished = settled >= LANES.length;

  return (
    <div className="relative">
      {/* The glow. Sits behind and outside the panel, which is what stops the card reading as a
          flat rectangle pasted onto the section. */}
      <div
        aria-hidden
        className="bg-primary/20 pointer-events-none absolute -inset-8 rounded-[2rem] blur-3xl"
      />

      <div
        className="border-border/60 bg-card relative overflow-hidden rounded-2xl border shadow-2xl"
        role="img"
        aria-label="A package update reaching a mixed fleet of servers: preflight, dry run, one canary server, confirmation and batches, then monitoring, Auto-Fix and weekly restore-tested backups."
      >
        {/* A 1px gradient along the top edge. Cheap, and it stops the border looking printed on. */}
        <div
          aria-hidden
          className="via-primary/60 absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent"
        />

        {/* ── Identity ──────────────────────────────────────────────────────── */}
        <div className="border-border/60 flex items-center justify-between gap-4 border-b px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="relative flex size-2 shrink-0">
                <span className="bg-emerald-500 absolute inline-flex size-2 rounded-full" />
                <span className="bg-emerald-500/60 absolute inline-flex size-2 animate-ping rounded-full" />
              </span>
              <span className="truncate font-medium">Update Packages</span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              Mixed fleet · Ubuntu, Debian and Rocky Linux
            </p>
          </div>

          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-500 ${
              finished ? 'bg-emerald-500/10 text-emerald-500' : 'bg-primary/10 text-primary'
            }`}
          >
            {finished ? 'Succeeded' : 'Running'}
          </span>
        </div>

        {/* ── The stages ────────────────────────────────────────────────────── */}
        <ul className="divide-border/60 divide-y">
          {LANES.map((lane, index) => {
            const done = index < settled;
            const working = index === settled && !finished;

            return (
              <li
                key={lane.label}
                className={`flex items-center gap-3 px-5 py-3 transition-all duration-500 ${
                  done || working ? 'opacity-100' : 'opacity-30'
                }`}
              >
                <span
                  className={`flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-500 ${
                    done ? 'bg-primary/10 text-primary' : 'bg-muted/40 text-muted-foreground'
                  }`}
                >
                  <lane.icon className="size-4" aria-hidden />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{lane.label}</span>
                  {/* Wraps on a phone rather than losing its end to an ellipsis. */}
                  <span className="text-muted-foreground block text-xs sm:truncate">{lane.state}</span>
                </span>

                <span className="shrink-0">
                  {done ? (
                    lane.live && finished ? (
                      // The one lane that never finishes, because protecting the data does not.
                      <span className="flex items-center gap-1.5 text-xs text-emerald-500">
                        <span className="relative flex size-1.5">
                          <span className="bg-emerald-500 absolute inline-flex size-1.5 rounded-full" />
                          <span className="bg-emerald-500/60 absolute inline-flex size-1.5 animate-ping rounded-full" />
                        </span>
                        Ongoing
                      </span>
                    ) : (
                      <Check className="size-4 text-emerald-500" aria-hidden />
                    )
                  ) : working ? (
                    <Loader2 className="text-primary size-4 animate-spin" aria-hidden />
                  ) : (
                    <Clock className="text-muted-foreground/40 size-4" aria-hidden />
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        {/* ── The line that carries the meaning ─────────────────────────────── */}
        <div className="border-border/60 bg-muted/20 border-t px-5 py-3.5">
          <p className="text-muted-foreground text-xs leading-relaxed">
            {finished ? (
              <>
                <span className="font-medium text-emerald-500">Every change recorded.</span> Problems
                surface on one server, not the whole fleet.
              </>
            ) : (
              'Checked on every server before anything changes.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
